/**
 * @module Services/Bff/Presenters/Combo
 * @desc Combo Presenter
 *
 */

import { formatMoney } from 'core/utilities';

import type { ComboResponse, PriceSpecification, ResellerPriceSpecification } from '../../@types';
import type { Combo } from 'core/domains';

function getImageUrl(imageObjects: ComboResponse['data']['imageObjects']) {
  const hasImages = imageObjects.length > 0;
  const featuredImage = imageObjects.find(({ featured }) => featured);
  const fallbackImage = hasImages ? imageObjects[0].large : '';

  return featuredImage ? featuredImage.large : fallbackImage;
}

function pricePresenter(
  priceSpecification: PriceSpecification,
  resellerPriceSpecification: ResellerPriceSpecification,
) {
  const { price: priceReseller = 0, profit = 0 } = resellerPriceSpecification;
  const { maxPrice = 0 } = priceSpecification;

  return {
    price: {
      maxPrice: formatMoney(maxPrice),
      maxPriceNum: maxPrice,
      price: formatMoney(priceReseller),
      priceNum: priceReseller,
      profit: formatMoney(profit),
      profitNum: profit,
    },
  };
}

function productsKitPresenter(
  products: ComboResponse['data']['productsKit'],
  comboQuantity: ComboResponse['data']['quantity'],
) {
  const sortedList = [...products]
    .sort((a) => (a.available ? -1 : 1))
    .sort((a, b) => b.quantity - a.quantity);

  return sortedList.map(
    ({ available, imageObjects, name, quantity = 0, quantityPerKit = 0, sku }) => ({
      available,
      code: parseInt(sku, 10),
      image: getImageUrl(imageObjects) || '',
      name,
      quantity: quantity || quantityPerKit,
      quantityPerKit,
      totalQuantity: quantityPerKit * comboQuantity,
    }),
  );
}

function productsChoiceKitPresenter(
  productsChoice: ComboResponse['data']['productsKitChoice'],
  comboQuantity: ComboResponse['data']['quantity'],
) {
  return productsChoice.map(
    ({ items, productStructureDescription, quantityPerKit = 0, structureCode }) => ({
      description: productStructureDescription,
      items: productsKitPresenter(items, comboQuantity),
      quantityPerKit,
      totalQuantity: comboQuantity * quantityPerKit,
      structureCode,
    }),
  );
}

const canAddComboValidate = (
  available: ComboResponse['data']['available'],
  choices: ComboResponse['data']['productsKitChoice'],
  comboQuantity: ComboResponse['data']['quantity'],
) => {
  const result = choices.map((choice) => {
    const selectedAmount = choice.items.reduce((sum, { quantity }) => sum + quantity, 0);

    return selectedAmount === comboQuantity * choice.quantityPerKit;
  });

  return available && !result.includes(false);
};

/**
 * Returns the formatted combo data from BFF to the store
 *
 */
export function comboPresenter({ data }: ComboResponse): Combo['data'] {
  const {
    available,
    description,
    imageObjects,
    name,
    priceSpecification,
    productsKit,
    productsKitChoice,
    quantity,
    resellerPriceSpecification,
    sku,
  } = data;

  const comboQuantity = quantity || 1;

  return {
    available,
    canAddCombo: canAddComboValidate(available, productsKitChoice, comboQuantity),
    choices: productsChoiceKitPresenter(productsKitChoice, comboQuantity),
    comboCode: parseInt(sku, 10) || 0,
    image: getImageUrl(imageObjects) || '',
    name: name.length > 0 ? name : description,
    products: productsKitPresenter(productsKit, comboQuantity),
    quantity: comboQuantity,
    ...pricePresenter(priceSpecification, resellerPriceSpecification),
  };
}
